/* eslint-disable max-lines */
import { chatCapacityStatusAtomFamily, evidenceInputActiveAtom, isShareModalOpenAtom, linkedPatientContextAtom, linkedSessionContextAtom, newChatModeAtom, showGeoBlockModalAtom, showSignUpModalAtom } from '@/atoms/chatAtoms';
import { activeSidebarChatIdAtom, sidebarChatIdAtomFamily } from '@/atoms/chatSidebarAtoms';
import { isSourceControlReadyForUser, sourceControlReadinessAtom } from '@/atoms/sourceControlAtoms';;
import {
  isTranscribingAtom,
  onChipClickAtom,
  onPinnedChipClickAtom,
  suggestedChipsAtom,
} from '@/atoms/evidenceChipsAtoms';
import { EvidenceSignUpBanner } from '@/components/chat/components/EvidenceSignUpBanner';
import { useChat } from '@/components/chat/hooks/useChat';
import { useChatQueue } from '@/components/chat/hooks/useChatQueue';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { useFormTemplateFill } from '@/components/chat/hooks/useFormTemplateFill';
import { useChatCooldown } from '@/components/chat/hooks/useChatCooldown';
import { useGlobalStreamTimeoutWatcher } from '@/components/chat/hooks/useGlobalStreamTimeoutWatcher';
import { ChatMode } from '@/components/chat/types/types';
import {
  IS_CHAT_CAPACITY_GUARD_ENABLED,
  IS_INSET_DESIGN_ENABLED,
  IS_TRANSCRIPT_CHIPS_ENABLED,
} from '@/features/flags/legacy-flags';
import {
  Banner,
  BannerAction,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { TypographyP2 } from '@heidi/design-system/typography';
import { ChatHistorySignupCtaModal } from '@/components/modal/ChatHistorySignupCtaModal';
import { EvidenceOnboardingModal } from '@/components/modal/evidence-onboarding-modal';
import { SignUpPromptModalChat } from '@/components/modal/sign-up-prompt-modal-chat';
import { useNoteOrDocumentInfo } from '@/components/ui/ask-ai-input/hooks/useNoteOrDocumentInfo';
import { EvidencePromptsBar, SuggestionsPeekSheet } from '@/components/ui/floating-ask-heidi';
import { DocumentSuggestionBanner } from '@/components/ui/session-ask-heidi/document-suggestion-banner';
import { useCanShareEvidenceChat } from '@/components/chat/hooks/useCanShareEvidenceChat';
import {
  useIsLinkedContextProvenanceGateKilled,
  useIsMidChatClinicalLinkPersistEnabled,
  useIsShareEvidenceChatEnabled,
} from '@/features/flags';
import {
  formatInteractiveWidgetSubmission,
  type InteractiveWidgetSubmission,
} from '@heidi/evidence';
import { startTracking, TrackingKey } from '@heidi/runtime/performance';
import { useSourceRecommendationBanner } from '@/hooks/useSourceRecommendationBanner';
import { EvidenceSBSInline } from '@/features/sideBySideEvidence';
import type { DocumentSuggestionPayload } from '@/helpers/clinical-tasks/documentCreationTask';
import { getCountryFromGeo } from '@/helpers/getCountryFromGeo';
import { useActivateRecommendedSources } from '@/hooks/useActivateRecommendedSources';
import { useMutateContinueChat } from '@/hooks/mutation/useMutateContinueChat';
import { useEvidenceChipsConfig } from '@/hooks/useEvidenceChipsConfig';
import { useEvidencePromptsBar } from '@/hooks/useEvidencePromptsBar';
import { useSessionStage } from '@/hooks/useSessionStage';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useIsKeyboardOpen } from '@/hooks/useIsKeyboardOpen';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { ProseDensityProvider } from '@heidi/answer-kit';
import { cn } from '@/lib/cn';
import type {
  ChipOrigin,
  ChipSurface,
  MessageEntryMethod,
} from '@/models/analytics/features/chat';
import type { PersonalisedSourcesControlProps } from '@/models/analytics/features/evidence-shortcuts';
import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { isChatContinueApiAvailable } from '@/utils/backendSurfaces';
import { logger } from '@/utils/logger/logger';
import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BlurMaskOverlay } from './components/BlurMaskOverlay';
import { ChatDebugPanel } from './components/ChatDebugPanel';
import { ChatEmptyConversation } from './components/ChatEmptyConversation';
import { ChatEmptyState } from './components/ChatEmptyState';
import { ChatHeader } from './components/ChatHeader';
import { ChatInput } from './components/ChatInput';
import { ChatTimeline } from './components/ChatTimeline';
import { EvidenceVerbSuggestions } from './components/evidence-verb-suggestions';
import { EvidenceChatSkeleton } from './components/EvidenceChatSkeleton';
import { EvidenceProTrialBanner } from './components/EvidenceProTrialToast';
import { EvidencePowerUserBanner } from '@/components/modal/evidence-power-user-modal';
import { ExperienceFeedbackNudge } from '@/components/experience-feedback/ExperienceFeedbackNudge';
import { FormTemplatePicker } from './components/FormTemplatePicker';
import { QueuedMessages } from './components/QueuedMessages';
import { ChatCapacityBanner } from './components/ChatCapacityBanner';
import { SourceRecommendationBanner } from './components/SourceRecommendationBanner';
import { StreamRecoveryIndicator } from './components/StreamRecoveryIndicator';
import { TrialSourceControlBanner } from './components/TrialSourceControlBanner';
import { useAutoScroll } from './hooks/useAutoScroll';
import { useChatContextLinking } from './hooks/useChatContextLinking';
import { useExperienceFeedbackSubmission } from './hooks/useExperienceFeedbackSubmission';
import { useExperienceFeedbackTrigger } from './hooks/useExperienceFeedbackTrigger';
import { artefactPreviewAtom, documentPreviewAtom } from '@heidi/evidence';
import { isEvidenceHomePage } from './utils/chatHelpers';
import { MedicalDirectorSidebar } from '@/integrations/medical-director/medicalDirectorSidebar';
import { useDocumentCreationSuggestionBanner } from '@/hooks/useDocumentCreationSuggestionBanner';
import { useUser } from '@heidi/workspace/data';

interface ChatRendererProps extends PersonalisedSourcesControlProps {
  sessionId?: string | null;
  chatId?: string | null;
  onClose?: () => void;
  showMobileMenu?: boolean;
  /** Whether to show the onboarding tooltip for source control. Disable for sidebar instances. */
  enableOnboardingTooltip?: boolean;
  /** Whether to show the evidence onboarding sources spotlight (step 2/3). Sidebar only. */
  enableSourcesSpotlight?: boolean;
  canViewChatHistory?: boolean;
  /** Initial query to auto-send when the component mounts (used for deep linking) */
  /** Whether to hide the chat header (used for iframe embedding) */
  hideHeader?: boolean;
  initialQuery?: string | null;
  /** Pre-uploaded attachment IDs to send with `initialQuery` (Work Evidence redirect). */
  initialAttachmentIds?: string[] | null;
  /** Display metadata for `initialAttachmentIds`. Sent with the first message. */
  initialAttachmentMetadata?: Array<{
    name: string;
    type: string;
    url?: string;
    attachment_id?: string;
  }> | null;
  /** Whether the chat is rendered as an embeddable widget */
  isWidget?: boolean;
  /** Whether the chat is a public shared link — suppresses the blur/signup CTA overlay */
  isPublicShare?: boolean;
  /** Optional side panel (e.g. CanvasPanel) rendered beside the content area, below the header */
  sidePanel?: React.ReactNode;
  /** Override for full-screen evidence mode. When true, uses viewport-based layout. Defaults to route detection. */
  forceFullScreenEvidence?: boolean;
  /**
   * True when this renderer sits inside `ChatWithCanvas`, which mounts
   * `PresentationPanel`. Defaults off so in-session, embed, and share fall
   * back to the artefact preview modal (EVI-2093).
   */
  canOpenPresentationPanel?: boolean;
  /** Replaces the default background so a host's chrome and this body read as one surface. */
  surfaceClassName?: string;
  /**
   * Chat mode to send. Sessionless hosts otherwise always use Evidence.
   * The inbox document bar sets this from in-session Evidence access.
   */
  mode?: ChatMode;
  /**
   * Keep a new sessionless chat on this page. The inbox document sheet sets
   * this so sending does not navigate to the Evidence chat route.
   */
  stayOnPage?: boolean;
  /**
   * Off-session chats are titled Evidence. The inbox document bar sets this
   * so a legacy chat on that page is titled Ask Heidi.
   */
  preferModeTitle?: boolean;
}

// Chat owns the Evidence verb catalogue. Its existing app-shell composition
// boundary exposes the resolved verbs needed by SessionWorkPanel, keeping Chat
// out of the locked projects feature. The analytics verb map is NOT re-exported
// here: it lives in `@/models/analytics/features/evidence` and consumers read it
// there, so Chat gains no coupling to `src/models` for it (chat-baseline K2).
export { useSessionResearchVerbs } from './components/evidence-verb-suggestions/hooks/useSessionResearchVerbs';
export { ChatMode };

export const ChatRenderer = ({
  sessionId,
  chatId,
  onClose,
  showMobileMenu,
  enableOnboardingTooltip = true,
  enableSourcesSpotlight = false,
  canViewChatHistory = true,
  initialQuery,
  initialAttachmentIds,
  initialAttachmentMetadata,
  hideHeader = false,
  isWidget = false,
  isPublicShare = false,
  sidePanel,
  forceFullScreenEvidence,
  canOpenPresentationPanel = false,
  personalisedSourceLogos = false,
  analyticsSurface,
  surfaceClassName,
  mode,
  stayOnPage = false,
  preferModeTitle = false,
}: ChatRendererProps) => {
  // Global timeout watcher - monitors all active streams across all chats
  useGlobalStreamTimeoutWatcher();

  const intl = useIntl();
  const router = useRouter();
  const isMobile = useIsMobile();
  const isKeyboardOpen = useIsKeyboardOpen({ enabled: isMobile });
  const [newMode, setNewMode] = useAtom(newChatModeAtom);
  const [hostedChatId, setHostedChatId] = useState<string | null>(null);
  const activeChatId = chatId ?? (stayOnPage ? hostedChatId : null);
  // Use session-scoped atom for sidebar chat selection (only relevant when sessionId exists)
  const setSidebarChatId = useSetAtom(sidebarChatIdAtomFamily(sessionId ?? ''));
  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);
  const isSessionless = !sessionId || router.pathname.includes('/chat');
  // Full-screen evidence is when we're on /scribe/chat or /chat routes (not in sidebar mode)
  // Can be overridden via forceFullScreenEvidence prop for routes like /s/[token]
  const isFullScreenEvidence = forceFullScreenEvidence ?? router.pathname.includes('/chat');
  // Explicit check for evidence home pages where two-row animations should appear
  const isOnEvidenceHomePage = isEvidenceHomePage(router.pathname);
  const { content, tabName, documentId } = useNoteOrDocumentInfo(sessionId ?? null);
  const [bannerDismissedAt, setBannerDismissedAt] = useState<number | null>(null);
  const [suggestionPreview, setSuggestionPreview] = useState<string | null>(null);
  const [populateValue, setPopulateValue] = useState<string | null>(null);
  const linkedSession = useAtomValue(linkedSessionContextAtom);
  const linkedPatient = useAtomValue(linkedPatientContextAtom);
  const [showSignUpModal, setShowSignUpModal] = useAtom(showSignUpModalAtom);
  const [_, setIsShareModalOpen] = useAtom(isShareModalOpenAtom);
  const isEvidenceInputActive = useAtomValue(evidenceInputActiveAtom);
  const setArtefactPreview = useSetAtom(artefactPreviewAtom);
  const setDocumentPreview = useSetAtom(documentPreviewAtom);
  const capacityStatus = useAtomValue(
    chatCapacityStatusAtomFamily(activeChatId ?? '')
  );
  const { isAuthenticated, isLoading: isAuthenticationLoading } =
    useIsAuthenticated();
  const {
    data: sourceControlUserId,
    refetch: refetchSourceControlUser,
  } = useUser((user) => user.user_ref_id ?? user.kinde_user_id ?? null);
  const sourceControlReadiness = useAtomValue(sourceControlReadinessAtom);
  const isSourceControlReady = isSourceControlReadyForUser(
    sourceControlReadiness,
    sourceControlUserId ?? null
  );
  const isSourceControlSubmissionBlocked =
    isAuthenticationLoading || !isSourceControlReady;

  const [showGeoBlockModal, setShowGeoBlockModalState] = useState(false);
  const [isRetryingSourceControl, setIsRetryingSourceControl] = useState(false);
  const setShowGeoBlockModalAtom = useSetAtom(showGeoBlockModalAtom);
  const retrySourceControlHydration = useCallback(async () => {
    setIsRetryingSourceControl(true);
    try {
      await refetchSourceControlUser();
    } catch (error) {
      logger.error('Failed to retry source control hydration', { error });
    } finally {
      setIsRetryingSourceControl(false);
    }
  }, [refetchSourceControlUser]);

  const { data: geoCountry, isLoading: isGeoCheckPending } = useQuery({
    queryKey: ['geo-country'],
    queryFn: getCountryFromGeo,
    enabled: !isAuthenticated,
    staleTime: Infinity,
  });
  const isAustralianUnauthenticated = !isAuthenticated && geoCountry === 'Australia';

  const isProvenanceGateKilled = useIsLinkedContextProvenanceGateKilled();

  const {
    conversation,
    sendMessage,
    isStreaming,
    isRecovering,
    abortStream,
    retryLastMessage,
    clearConversation,
    resetForNewChat,
    chatError,
    error,
    isCreatingChat,
    isFetchingChat,
    messageHistory,
    chatMeta,
  } = useChat(
    sessionId ?? null,
    content,
    activeChatId,
    tabName,
    documentId,
    // Resolved here and threaded down: Chat.tsx already holds the quarantine
    // pair to @/features/flags, so the send hooks stay free of a new outbound
    // coupling (the Chat quarantine sets are shrink-only).
    isProvenanceGateKilled,
    mode,
    stayOnPage ? setHostedChatId : undefined
  );
  const resolvedMode = mode ?? (isSessionless ? ChatMode.EVIDENCE : newMode);

  // Cursor-style queue for messages sent while streaming.
  // Hosted chats keep the chatId prop null and adopt hostedChatId internally,
  // so the queue and capacity follow activeChatId rather than the prop.
  const stateKey = activeChatId
    ? `chat-${activeChatId}`
    : sessionId
      ? `session-${sessionId}-new`
      : 'sessionless';
  const { active: rateLimitCooldownActive, until: rateLimitCooldownUntil } =
    useChatCooldown(stateKey);
  const {
    queuedMessages,
    autoSendEnabled,
    hasQueuedMessages,
    queueOrSend,
    removeFromQueue,
    sendNow,
    toggleAutoSend,
    updateQueuedMessage,
    promoteMessage,
  } = useChatQueue({
    stateKey,
    isStreaming: isStreaming || isRecovering,
    sendMessage,
    abortStream,
  });

  const {
    isFormTemplatePickerOpen,
    closeFormTemplatePicker,
    handleFillVerbClick,
    handleLinkSessionForFill,
    handleFormTemplateSelected,
  } = useFormTemplateFill({
    sendMessage,
    setSuggestionPreview,
    setPopulateValue,
    sendBlocked: rateLimitCooldownActive,
  });

  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const latestGroupRef = useRef<HTMLDivElement>(null);
  const conversationOverlayContainerRef = useRef<HTMLDivElement>(null);
  const chatPaneRef = useRef<HTMLDivElement>(null);
  const chatInputAreaRef = useRef<HTMLDivElement>(null);

  // Chips state from atoms (for sidebar display)
  const suggestedChips = useAtomValue(suggestedChipsAtom);
  const isTranscribing = useAtomValue(isTranscribingAtom);
  const onChipClick = useAtomValue(onChipClickAtom);
  const onPinnedChipClick = useAtomValue(onPinnedChipClickAtom);

  // Read pinnedChips directly from TanStack Query cache so deletions
  // reflect in real-time even when FloatingAskHeidiWithChips is unmounted
  const {
    pinnedChips,
    handleRemovePermanentChip,
  } = useEvidenceChipsConfig();

  const isTranscriptChipsEnabled = IS_TRANSCRIPT_CHIPS_ENABLED();
  const { isShowing: showingDocumentCreationSuggestions } = useDocumentCreationSuggestionBanner(sessionId ?? undefined, {
    trackImpressions: false,
  });

  // When chips are killed, only pinned shortcuts belong in the peek panel (EVI-2114).
  const hasSuggestedChips = suggestedChips.length > 0;
  const peekSuggestedChips = isTranscriptChipsEnabled ? suggestedChips : [];
  const peekIsTranscribing = isTranscriptChipsEnabled && isTranscribing;
  const showShortcutsPeekChrome = !isTranscriptChipsEnabled;
  const hasPeekContent =
    peekSuggestedChips.length > 0 || pinnedChips.length > 0 || peekIsTranscribing;
  const hasChipsToShow =
    hasPeekContent && !isSessionless && !showingDocumentCreationSuggestions;

  // Track scroll position for "more content below" indicator
  // Also handles scrolling to top when follow-up queries are sent
  const { hasMoreContentBelow, scrollToBottom } = useAutoScroll({
    scrollAreaRef,
    conversation,
    latestGroupRef,
    isStreaming: isStreaming || isRecovering,
  });

  const isMidChatClinicalLinkPersistEnabled = useIsMidChatClinicalLinkPersistEnabled();
  const hasChatMessages =
    chatMeta.hasMessages ||
    conversation.timeline.some((entry) => entry.type === 'user_message');

  // Context linking - derives locked state and syncs from backend
  const { isContextLocked } = useChatContextLinking({
    chatId: chatId ?? null,
    isLoading: isStreaming || isCreatingChat || isFetchingChat,
    sessionId: chatMeta.sessionId,
    patientProfileId: chatMeta.patientProfileId,
    hasMessages: hasChatMessages,
    isMidChatClinicalLinkPersistEnabled,
  });

  // Experience feedback nudge - count completed AI responses for trigger logic
  // text_item entries represent assistant responses in the timeline
  const completedQueryCount = useMemo(() => {
    if (!conversation.timeline) return 0;
    return conversation.timeline.filter(
      (entry) => entry.type === 'text_item'
    ).length;
  }, [conversation.timeline]);

  // Experience feedback trigger and submission hooks
  const {
    shouldShowNudge,
    showNudge: showFeedbackNudge,
    snooze: handleFeedbackSnooze,
    hideNudge: handleFeedbackDismiss,
  } = useExperienceFeedbackTrigger({
    queryCount: completedQueryCount,
    isStreaming,
    isAuthenticated,
    chatId,
  });

  const {
    easeRating: feedbackEaseRating,
    usefulnessRating: feedbackUsefulnessRating,
    detail: feedbackDetail,
    isRatingSubmitted: isFeedbackRatingSubmitted,
    submitEaseRating: handleFeedbackEaseRatingSubmit,
    submitUsefulnessRating: handleFeedbackUsefulnessRatingSubmit,
    setDetail: setFeedbackDetail,
    submitFeedback: handleFeedbackSubmit,
    isSubmitDisabled: isFeedbackSubmitDisabled,
  } = useExperienceFeedbackSubmission({
    queryCount: completedQueryCount,
    chatId: chatId ?? undefined,
    sessionId: sessionId ?? undefined,
  });

  const handleSendMessage = useCallback(
    (
      message: string,
      files?: File[], // Legacy: raw files (deprecated)
      attachmentIds?: string[], // Pre-uploaded attachment IDs
      fileMetadata?: Array<{
        name: string;
        type: string;
        url?: string;
        attachment_id?: string;
      }>,
      entryMethod?: MessageEntryMethod,
      chipLabel?: string,
      chipCategory?: string,
      chipOrigin?: ChipOrigin,
      chipSurface?: ChipSurface,
    ) => {
      if (isAustralianUnauthenticated) {
        setShowGeoBlockModalState(true);
        setShowGeoBlockModalAtom(true);
        return;
      }

      // Queue when streaming; otherwise send immediately.
      queueOrSend(message, files, attachmentIds, fileMetadata, entryMethod, chipLabel, chipCategory, chipOrigin, chipSurface).catch((error: unknown) => {
        logger.error('Failed to send/queue message in chat', {
          error,
          sessionId: sessionId ?? 'sessionless',
          messageLength: message?.length,
        });
      });
    },
    [
      queueOrSend,
      sessionId,
      isAustralianUnauthenticated,
      // `useSetAtom` returns a stable setter, so this never re-creates the
      // callback — it is listed to satisfy the exhaustive-deps check honestly
      // rather than silencing it.
      setShowGeoBlockModalAtom,
    ]
  );

  const { activateRecommendedSources } = useActivateRecommendedSources();

  // Bridge an inline interactive widget's submission into the chat send
  // pipeline so the LLM sees the user's choices as a real turn and can
  // continue the differential. Forwarded down through ChatTimeline →
  // InteractiveWidgetBlock; the formatter switches per ``kind`` inside the
  // submission module so this site stays kind-agnostic.
  const handleInteractiveWidgetSubmit = useCallback(
    (submission: InteractiveWidgetSubmission) => {
      handleSendMessage(formatInteractiveWidgetSubmission(submission));
    },
    [handleSendMessage]
  );

  const handleDocumentSuggestionBannerCtaClick = useCallback(
    (item: DocumentSuggestionPayload) => {
      void activateRecommendedSources();
      setActiveSidebarChatId(null);
      setSidebarChatId(null);
      startTracking({
        key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
        metadata: {
          surface: 'document_suggestion_banner',
          entry_method: 'document_suggestion_banner',
          chip_category: 'document_suggestion',
          session_id: sessionId ?? linkedSession?.id ?? null,
        },
      });
      handleSendMessage(
        item.evidencePrompt,
        undefined,
        undefined,
        undefined,
        'document_suggestion_banner',
        item.bannerTitle,
        'document_suggestion'
      );
    },
    [activateRecommendedSources, setActiveSidebarChatId, setSidebarChatId, sessionId, linkedSession?.id, handleSendMessage]
  );

  // Handler for selecting follow-up questions from chips
  const handleSelectFollowUp = useCallback(
    (question: string, entryMethod: MessageEntryMethod = 'follow_up_chip') => {
      // Send the follow-up question as a new message. entryMethod defaults to
      // 'follow_up_chip' but callers (e.g. "Check other sources") can override.
      sendMessage({ content: question, entryMethod }).catch((error: unknown) => {
        logger.error('Failed to send follow-up question', {
          error,
          sessionId: sessionId ?? 'sessionless',
          questionLength: question?.length,
        });
      });
    },
    [sendMessage, sessionId]
  );

  // Fallback for when FloatingAskHeidiWithChips is unmounted (sidebar open)
  // and atom handlers are null — sends the chip query into the current chat.
  const handleChipSend = useCallback(
    (chip: {
      query: string;
      label?: string;
      category?: string;
      isPermanent?: boolean;
      isPinned?: boolean;
      origin?: ChipOrigin;
    }) => {
      if (chip.category === 'document_draft') {
        void activateRecommendedSources();
      }
      const isShortcut = chip.isPermanent === true || chip.isPinned === true;
      const entryMethod: MessageEntryMethod | undefined =
        chip.category === 'document_draft'
          ? 'document_suggestion_chip'
          : isShortcut
            ? 'shortcut_chip'
            : chip.isPermanent === false || chip.isPinned === false
              ? 'suggestion_chip'
              : undefined;
      const chipCategoryForAnalytics =
        chip.category === 'document_draft' ? 'document_suggestion' : chip.category;
      // Only propagate origin for shortcut-type chips — other chip types don't
      // have a meaningful origin bucket.
      const chipOrigin = isShortcut ? chip.origin : undefined;
      // handleChipSend is invoked from the floating suggestion bar above the
      // chat input; the '/' menu uses ChatInput.handleProtocolChipClick. All
      // chip-type entries dispatched from this handler share the same surface,
      // including `document_suggestion_chip` (which surfaces in the same bar).
      const chipSurface: 'suggestion_bar' | undefined = entryMethod
        ? 'suggestion_bar'
        : undefined;
      if (entryMethod) {
        startTracking({
          key: TrackingKey.EVIDENCE_IN_SESSION_SUGGESTION_CLICK,
          metadata: {
            surface: chipSurface,
            entry_method: entryMethod,
            chip_category: chipCategoryForAnalytics,
            chip_origin: chipOrigin,
            session_id: sessionId ?? linkedSession?.id ?? null,
          },
        });
      }
      handleSendMessage(
        chip.query,
        undefined,
        undefined,
        undefined,
        entryMethod,
        chip.label,
        chipCategoryForAnalytics,
        chipOrigin,
        chipSurface,
      );
    },
    [handleSendMessage, activateRecommendedSources, sessionId, linkedSession?.id]
  );

  /**
   * Populates the chat input with a starter text from verb suggestions
   * without sending the message. The user can then complete and send.
   */
  const handlePopulateInput = useCallback(
    (starter: string) => {
      setSuggestionPreview(null);
      setPopulateValue(starter);
    },
    []
  );

  // Reset transient UI state when chat changes, user authenticates, or route changes.
  // Watching router.asPath ensures preview atoms are cleared when navigating between
  // out-of-session (/scribe/chat) and in-session (/scribe/session/[id]) contexts,
  // preventing stale PDF preview state from leaking across contexts (EVI-856).
  //
  // PHI safety: also clears the patient-summary delivery atoms so that a
  // half-started delivery for patient A (which holds their phone number,
  // DOB-derived PDF password, and name) does not survive a navigation into a
  // different session/patient context.
  useEffect(() => {
    setBannerDismissedAt(null);
    setPopulateValue(null);
    setArtefactPreview({ isOpen: false, url: null, filename: null, title: null, password: null });
    setDocumentPreview({ isOpen: false, documentId: null });
  }, [
    chatId,
    isAuthenticated,
    router.asPath,
    setArtefactPreview,
    setDocumentPreview,
  ]);

  // Auto-send initial query from URL params (deep linking support)
  // Track which query was sent to prevent re-sends on remount while allowing different queries
  const initialQuerySentRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      initialQuery &&
      initialQuerySentRef.current !== initialQuery &&
      !chatId &&
      !isStreaming &&
      !isCreatingChat &&
      !isGeoCheckPending &&
      !isSourceControlSubmissionBlocked
    ) {
      initialQuerySentRef.current = initialQuery;
      handleSendMessage(
        initialQuery,
        undefined,
        initialAttachmentIds?.length ? initialAttachmentIds : undefined,
        initialAttachmentMetadata?.length ? initialAttachmentMetadata : undefined
      );
    }
  }, [
    initialQuery,
    initialAttachmentIds,
    initialAttachmentMetadata,
    chatId,
    isStreaming,
    isCreatingChat,
    handleSendMessage,
    isGeoCheckPending,
    isSourceControlSubmissionBlocked,
  ]);

  const isEmpty = !conversation.timeline || conversation.timeline.length === 0;
  const showSessionlessLayout = isSessionless && isEmpty && !chatId;

  // In-session verb chips empty state: shown when there's no chat yet AND no
  // transcript-based suggestions. Pinned chips alone do not suppress this state.
  // When active, the SuggestionsPeekSheet (including pinned chips) is suppressed
  // to avoid visual clutter. Pinned chips remain accessible via "/" command and protocols menu.
  const showInSessionVerbChips =
    !isSessionless && isEmpty && !chatId && !hasSuggestedChips;
  // Two-row animations (QuestionSuggestions) should only appear on evidence home pages
  // These are specifically /scribe/chat or /chat routes (without a chat ID)
  // Also show in widget mode (/chat/embed)
  const showAnimations = (isOnEvidenceHomePage || isWidget) && !chatId;

  // Show blur overlay with signup CTA modal when unauthenticated and viewing a past chat,
  // or when geo-blocked (Australia unauthenticated)
  const showBlurOverlayWithSignupCtaModal =
    !isPublicShare &&
    ((!isAuthenticated && !!chatId && !canViewChatHistory) || showGeoBlockModal) &&
    !showSignUpModal;

  // Show evidence sign-up banner above chat input every 4 messages when unauthenticated.
  // "Maybe later" dismisses it until another 4 messages are sent.
  const messageHistoryCount = useMemo(() => messageHistory.length, [messageHistory]);
  const showEvidenceSignUpBanner =
    !isAuthenticated &&
    messageHistoryCount > 0 &&
    (messageHistoryCount === 1 ||
    messageHistoryCount % 4 === 0) &&
    bannerDismissedAt !== messageHistoryCount;

  // Dynamically compute blur mask cutoff based on the branded title (first user message).
  // This prevents hardcoded pixel gaps and adapts to multi-line titles.
  // For geo-blocked chats (no branded title), start blur from the top.
  const [blurMask, setBlurMask] = useState<{ clearUntilPx: number; fadePx: number }>({
    clearUntilPx: showGeoBlockModal ? 0 : 52,
    fadePx: 12,
  });

  useEffect(() => {
    if (!showBlurOverlayWithSignupCtaModal) return;

    const container = conversationOverlayContainerRef.current;
    const viewport = scrollViewportRef.current;
    if (!container) return;

    let rafId: number | null = null;

    const update = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        const titleEl =
          (container.querySelector('[data-chat-branded-title="true"]') as HTMLElement | null) ??
          (viewport?.querySelector('[data-chat-branded-title="true"]') as HTMLElement | null);
        if (!titleEl) return;

        const containerRect = container.getBoundingClientRect();
        const titleRect = titleEl.getBoundingClientRect();

        // Compute the title's bottom position relative to the overlay container.
        const bottom = Math.max(0, titleRect.bottom - containerRect.top);
        // Small fade-in so the blur transition isn't harsh.
        const fadePx = 12;
        setBlurMask({
          clearUntilPx: Math.round(bottom),
          fadePx,
        });
      });
    };

    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(container);
    if (viewport) ro.observe(viewport);

    viewport?.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      ro.disconnect();
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [showBlurOverlayWithSignupCtaModal]);

  const handleNewChatFromHistory = useCallback(() => {
    clearConversation();
    const basePath = router.pathname.includes('/scribe/chat') ? '/scribe/chat' : '/chat';
    void router.push(basePath);
  }, [clearConversation, router]);

  // Handlers for session chat history dropdown (scribe session context)
  // Use atom instead of router.push to avoid triggering routeChangeStart loading state
  const handleSelectChatFromSession = useCallback(
    (selectedChatId: string) => {
      startTracking({
        key: TrackingKey.EVIDENCE_CHAT_OPEN,
        metadata: {
          route: 'scribe_session',
          selected_chat_id: selectedChatId,
          session_id: sessionId ?? null,
        },
      });
      setSidebarChatId(selectedChatId);
    },
    [setSidebarChatId, sessionId]
  );

  const { trackNewChatClicked, trackChatError } = useEvidenceAnalytics();

  const loadErrorTrackedRef = useRef(false);
  useEffect(() => {
    if (chatId && chatError && !loadErrorTrackedRef.current) {
      loadErrorTrackedRef.current = true;
      trackChatError({
        errorType: 'load_chat',
        errorMessage:
          chatError instanceof Error ? chatError.message : 'Failed to load chat',
        chatId,
      });
    }
  }, [chatId, chatError, trackChatError]);

  const handleNewChatFromSession = useCallback(() => {
    trackNewChatClicked('sidebar');
    // Use resetForNewChat instead of clearConversation: this clears the
    // session-new key (the upcoming "new chat" state) rather than the current
    // chat key (chat-{chatId}). This preserves the old chat's in-memory state
    // so the user can navigate back without hitting a skeleton/loading state.
    resetForNewChat();
    setSidebarChatId(null);
  }, [trackNewChatClicked, resetForNewChat, setSidebarChatId]);

  const { mutate: mutateContinueChat, isPending: isMigrating } = useMutateContinueChat();

  const handleContinueChat = useCallback(() => {
    if (!chatId) return;
    mutateContinueChat(chatId, {
      onSuccess: (data) => {
        if (sessionId) {
          setSidebarChatId(data.chat_id);
        } else {
          void router.push(`/scribe/chat/${data.chat_id}`, undefined, { shallow: true });
        }
      },
    });
  }, [chatId, mutateContinueChat, sessionId, setSidebarChatId, router]);

  // `POST /chat/{id}/continue` stays in ml-scribe's cloud-only chat router. The
  // on-prem engine never emits a capacity chunk, so the banner should not show
  // there at all; if it ever does, it starts a new chat rather than 404ing.
  const capacityBannerHandler = useMemo(
    () =>
      chatId && isChatContinueApiAvailable
        ? handleContinueChat
        : isSessionless ? handleNewChatFromHistory : handleNewChatFromSession,
    [chatId, isSessionless, handleContinueChat, handleNewChatFromHistory, handleNewChatFromSession]
  );

  // Visibility-only — banner component owns analytics + impression tracking.
  const { shouldShow: isSourceRecommendationBannerVisible } =
    useSourceRecommendationBanner('chat', { trackImpressions: false });

  // Evidence prompts bar for sidebar mode (GRO-2829)
  const effectiveSessionId = sessionId ?? linkedSession?.id ?? '';
  const sessionStage = useSessionStage();
  const evidencePromptsBar = useEvidencePromptsBar({
    sessionId: effectiveSessionId,
    sessionStage,
    suggestedChips,
    pinnedChips,
    isBlocked: isSourceRecommendationBannerVisible,
  });

  const isMedicalDirector = MedicalDirectorSidebar.isMedicalDirector()

  /** Evidence sidebar / panel chat: hide duplicate considerations & peek above the footer input */
  const hidePeekAboveEvidenceInput = enableSourcesSpotlight;

  // Handle opening the share modal (works for both authenticated and anonymous users)
  const isShareEvidenceChatEnabled = useIsShareEvidenceChatEnabled();
  const canShareEvidenceChat = useCanShareEvidenceChat({
    chatId,
    backendSessionId: chatMeta.sessionId,
    backendPatientProfileId: chatMeta.patientProfileId,
    isShareEnabled: isShareEvidenceChatEnabled,
    isMidChatPersistEnabled: isMidChatClinicalLinkPersistEnabled,
  });
  const handleShare = useCallback(() => {
    if (chatId && canShareEvidenceChat) {
      setIsShareModalOpen(true);
    }
  }, [chatId, setIsShareModalOpen, canShareEvidenceChat]);

  const shouldAutoFocus = sidebarOpenTab === SidebarTab.ASK_HEIDI_V2;

  const handleTryWithResearch = useCallback(() => {
    try {
      // Find the last user message from the timeline
      const lastUserMessage = conversation.timeline
        .slice()
        .reverse()
        .find((entry) => entry.type === 'user_message');

      if (!lastUserMessage || lastUserMessage.type !== 'user_message') {
        logger.warn('No user message found to retry with research');
        return;
      }

      // Switch to research mode (for UI feedback) - only if not sessionless
      if (!isSessionless) {
        setNewMode(ChatMode.EVIDENCE);
      }

      // Resend the last user message with explicit EVIDENCE mode
      sendMessage({
        content: lastUserMessage.content,
        overrideMode: ChatMode.EVIDENCE,
      }).catch((error: unknown) => {
        logger.error('Failed to resend message with research mode', {
          error,
          sessionId: sessionId ?? 'sessionless',
          messageContent: lastUserMessage.content,
        });
      });
    } catch (error: unknown) {
      logger.error('Failed to try with research in chat', {
        error,
        sessionId: sessionId ?? 'sessionless',
      });
    }
  }, [
    conversation.timeline,
    sendMessage,
    sessionId,
    setNewMode,
    isSessionless,
  ]);

  const handleRetryLastMessage = useCallback(() => {
    try {
      retryLastMessage();
    } catch (error: unknown) {
      logger.error('Failed to retry last message in chat', {
        error,
        sessionId: sessionId ?? 'sessionless',
      });
    }
  }, [retryLastMessage, sessionId]);

  const conversationContent = useMemo(() => {
    const hasCachedState = conversation.timeline && conversation.timeline.length > 0;

    // Show error state if chat failed to load
    if (chatId && chatError && !hasCachedState) {
      return (
        <div className="flex h-full items-center justify-center p-4">
          <div className="flex flex-col items-center gap-4 text-center">
            <TypographyP2 className="text-red-600">
              <FormattedMessage
                defaultMessage="Failed to load chat. Please try again."
                id="EIai2XrBRU"
                description="Error shown when an existing chat cannot be loaded"
              />
            </TypographyP2>
            {error && (
              <TypographyP2 className="text-sm text-text-secondary">{error}</TypographyP2>
            )}
          </div>
        </div>
      );
    }

    // Show skeleton while fetching chat data (handles both initial load and cached data refetch)
    // This prevents flicker when navigating from /chat to /chat/[id]
    if (chatId && !hasCachedState && isFetchingChat) {
      return <EvidenceChatSkeleton variant="chat" />;
    }

    // RECOVERING WITH EMPTY TIMELINE: page was refreshed mid-stream, backend
    // is still processing. Show the loading skeleton with recovery indicator
    // so the user sees familiar loading UI plus the reconnecting status.
    if (chatId && !hasCachedState && !isFetchingChat && isRecovering) {
      return (
        <div className="flex h-full flex-col">
          <EvidenceChatSkeleton variant="chat" />
          <div className="flex justify-center pb-8">
            <StreamRecoveryIndicator />
          </div>
        </div>
      );
    }

    // EXISTING EMPTY CHAT: chatId exists but no messages after loading completes
    if (chatId && !hasCachedState && !isFetchingChat && !isCreatingChat) {
      return <ChatEmptyConversation />;
    }

    // NEW CHAT: No chatId, show greeting with logo animation
    // In-session: also show verb chips when no transcript-based suggestions are available
    if (!chatId && !hasCachedState && !isCreatingChat) {
      return (
        <ChatEmptyState
          isSessionless={isSessionless}
          pageOpenTrackingRoute={
            router.pathname.includes('/scribe/chat')
              ? 'scribe_chat'
              : isSessionless
                ? 'chat'
                : 'scribe_session'
          }
          onPopulateInput={
            !isSessionless && !hasSuggestedChips
              ? handlePopulateInput
              : undefined
          }
          onFillVerbClick={isAuthenticated ? handleFillVerbClick : undefined}
        />
      );
    }

    return (
      <ChatTimeline
        ref={scrollAreaRef}
        conversation={conversation}
        isStreaming={isStreaming}
        isRecovering={isRecovering}
        latestGroupRef={latestGroupRef}
        timelineRef={timelineRef}
        scrollViewportRef={scrollViewportRef}
        onRetryLastMessage={handleRetryLastMessage}
        onTryWithResearch={handleTryWithResearch}
        sessionId={sessionId ?? null}
        hasMoreContentBelow={hasMoreContentBelow}
        onScrollToBottom={scrollToBottom}
        isFullScreenEvidence={isFullScreenEvidence}
        chatId={chatId ?? undefined}
        chatTitle={chatMeta.title}
        onSelectFollowUp={handleSelectFollowUp}
        onAbortStream={abortStream}
        onShare={isSessionless && canShareEvidenceChat ? handleShare : undefined}
        onInteractiveWidgetSubmit={handleInteractiveWidgetSubmit}
        isAuthenticated={isAuthenticated}
        rateLimitCooldownUntil={rateLimitCooldownUntil}
        canOpenPresentationPanel={canOpenPresentationPanel}
      />
    );
  }, [
    chatId,
    chatError,
    chatMeta.title,
    error,
    conversation,
    isStreaming,
    isRecovering,
    abortStream,
    handleRetryLastMessage,
    handleTryWithResearch,
    isSessionless,
    sessionId,
    isCreatingChat,
    isFetchingChat,
    hasMoreContentBelow,
    scrollToBottom,
    isFullScreenEvidence,
    handleSelectFollowUp,
    handleShare,
    handleInteractiveWidgetSubmit,
    isAuthenticated,
    handlePopulateInput,
    hasSuggestedChips,
    canShareEvidenceChat,
    handleFillVerbClick,
    router.pathname,
    canOpenPresentationPanel,
  ]);

  // ── Shared JSX blocks (used across mobile/desktop/session layout branches) ──

  const renderQueueBlock = (className?: string) =>
    hasQueuedMessages && (
      <QueuedMessages
        messages={queuedMessages}
        autoSendEnabled={autoSendEnabled}
        onSendNow={sendNow}
        onRemove={removeFromQueue}
        onEdit={updateQueuedMessage}
        onMoveUp={promoteMessage}
        onToggleAutoSend={toggleAutoSend}
        className={className}
      />
    );

  const signUpBanner = (
    <EvidenceSignUpBanner
      className="mb-4"
      show={showEvidenceSignUpBanner}
      onMaybeLater={() => setBannerDismissedAt(messageHistoryCount)}
    />
  );

  const blurOverlay = showBlurOverlayWithSignupCtaModal && (
    <BlurMaskOverlay
      clearUntilPx={blurMask.clearUntilPx}
      fadePx={blurMask.fadePx}
    />
  );

  const baseChatInputProps = {
    onSend: handleSendMessage,
    onAbort: abortStream,
    isLoading: isStreaming || isCreatingChat || isGeoCheckPending,
    disabled:
      isSourceControlSubmissionBlocked ||
      showBlurOverlayWithSignupCtaModal ||
      rateLimitCooldownActive ||
      (IS_CHAT_CAPACITY_GUARD_ENABLED() && capacityStatus === 'exceeded'),
    autoFocus: shouldAutoFocus,
    isSessionless,
    chatId,
    enableOnboardingTooltip: isAuthenticated && enableOnboardingTooltip,
    compactBadges: !isSessionless,
    isContextLocked,
    isGenerating: isStreaming || isRecovering,
  };
  const renderSourceControlErrorBanner = (className?: string) =>
    sourceControlReadiness.status === 'blocked' ? (
      <Banner
        subVariant="error"
        role="alert"
        data-testid="source-control-readiness-error"
        className={className}
      >
        <BannerContent>
          <BannerTitle>
            <FormattedMessage
              defaultMessage="We couldn't load your Evidence sources"
              description="Evidence composer error when saved source selections fail to load"
              id="evidence.sourceControl.loadFailed"
            />
          </BannerTitle>
          <BannerDescription>
            <FormattedMessage
              defaultMessage="Check your connection and try again."
              id="xUnZ3uBJCE"
            />
          </BannerDescription>
        </BannerContent>
        <BannerAction>
          <Button
            variant="outline"
            size="sm"
            disabled={isRetryingSourceControl}
            onClick={() => void retrySourceControlHydration()}
          >
            <FormattedMessage defaultMessage="Try again" id="FazwRldA7z" />
          </Button>
        </BannerAction>
      </Banner>
    ) : null;

  const sessionlessChatInputProps = {
    ...baseChatInputProps,
    popoverSide: 'bottom' as const,
    showInlineSuggestions: showAnimations,
    suggestionPreview,
    populateValue,
    onPopulateConsumed: () => setPopulateValue(null),
    showDisclaimer: true,
  };

  return (
    // Chat is the surface that knows its own role, so it publishes the prose
    // density for everything below — the question bubble, the answer, and the
    // callouts inside it all resolve one scale instead of each guessing.
    // Full-screen is a primary reading surface (16px) at ANY device width,
    // mobile included; the sidebar is a reference panel beside the user's real
    // work, so it reads tighter (14px).
    <ProseDensityProvider density={isFullScreenEvidence ? 'comfortable' : 'compact'}>
    <div
      ref={chatPaneRef}
      className={cn('relative flex size-full flex-col', isMobile && isMedicalDirector && 'pt-10', surfaceClassName ?? (IS_INSET_DESIGN_ENABLED() ? 'bg-surface-shell' : isSessionless ? 'bg-surface-page' : 'bg-surface-container'))}
      data-chat-pane="true"
    >
      {/* Evidence onboarding modal - shown once for authenticated users on full-screen evidence */}
      {isAuthenticated && (
        <EvidenceOnboardingModal isFullScreenEvidence={isFullScreenEvidence} />
      )}

      {/* Signup modal - can be triggered from other components */}
      <SignUpPromptModalChat
        isOpen={showSignUpModal}
        onClose={() => {
          setShowSignUpModal(false);
        }}
        query={messageHistory.findLast(m => m.role === 'user')?.content ?? undefined}
        chatId={chatId ?? undefined}
      />

      {/* Signup CTA modal for viewing past chats when unauthenticated */}
      <ChatHistorySignupCtaModal
        isOpen={showBlurOverlayWithSignupCtaModal}
        portalContainer={chatPaneRef.current}
      />

      {/* Full-pane blur for geo-blocked users (covers entire chat area including empty state) */}
      {showGeoBlockModal && !showSignUpModal && (
        <BlurMaskOverlay clearUntilPx={0} fadePx={0} className="z-30" />
      )}

      {/* Header area - fixed height (can be hidden via hideHeader prop) */}
      {!hideHeader && (
        <ChatHeader
          onClose={onClose || (() => {})}
          showMobileMenu={showMobileMenu}
          mode={resolvedMode}
          sessionId={sessionId}
          currentChatId={chatId}
          onNewChat={handleNewChatFromSession}
          onSelectChat={handleSelectChatFromSession}
          isChatEmpty={conversation.timeline.length === 0}
          chatTitle={chatMeta.title}
          backendSessionId={chatMeta.sessionId}
          backendPatientProfileId={chatMeta.patientProfileId}
          isWidget={isWidget}
          isStreaming={isStreaming}
          preferModeTitle={preferModeTitle}
        />
      )}

      {/* Mobile close button — full-screen chat route only (ChatHeader renders empty on mobile) */}
      {isMobile && !hideHeader && isAuthenticated && (
        <div className="flex shrink-0 justify-end px-2 py-0.5">
          <Button
            variant="ghost"
            size="lg"
            iconOnly
            onClick={onClose ?? (() => router.back())}
            aria-label={intl.formatMessage({
              defaultMessage: 'Close chat',
              id: 'Eh3W4ZF0TA',
              description: 'Accessible label for the button that closes the chat',
            })}
          >
            <X size={16} />
          </Button>
        </div>
      )}

      {/* Content + optional side panel row */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
      {/* Top fade gradient - matches the one from ChatHeader but scoped to chat column only */}
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-10 h-2 bg-gradient-to-b sm:h-3',
          IS_INSET_DESIGN_ENABLED() ? 'from-surface-shell to-surface-shell/0' : isSessionless ? 'from-surface-page to-surface-page/0' : 'from-surface-container to-surface-container/0'
        )}
        aria-hidden="true"
      />
      {showSessionlessLayout ? (
        isMobile ? (
          // Mobile layout: Two states controlled by isEvidenceInputActive
          // Idle: welcome text + verb chips (vertical) + input at bottom
          // Active: full-view suggestions above input (no welcome/verbs)
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Top section: Welcome text + verb chips — hidden when input is active */}
            {!isKeyboardOpen && (
              <div className="relative flex flex-1 flex-col items-start justify-center px-4">
                {conversationContent}
                {/* Verb chips: stacked vertically, left-aligned, below greeting */}
                {showAnimations && (
                  <div className="mt-6 w-full">
                    <EvidenceVerbSuggestions onPopulateInput={handlePopulateInput} onSuggestionHover={setSuggestionPreview} onFillVerbClick={isAuthenticated ? handleFillVerbClick : undefined} isMobile />
                  </div>
                )}
                {blurOverlay}
              </div>
            )}

            {/* Bottom section: Queue + Input (expands to fill when active via mobileFullView) */}
            <div className="shrink-0 px-4 pb-2">
              {renderQueueBlock('mb-3')}
              {signUpBanner}
              {renderSourceControlErrorBanner('mb-3')}
              <ChatInput {...sessionlessChatInputProps} mobileFullView={isEvidenceInputActive} />
            </div>
          </div>
        ) : (
          // Desktop layout: Center of viewport sits between heading and input
          // Bottom padding creates space for the verb suggestions popover to expand into
          <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 pb-[160px]">
            <div className="relative flex w-full max-w-3xl flex-col items-center">
              {conversationContent}
              {blurOverlay}
            </div>
            <div className="w-full max-w-3xl shrink-0 space-y-4">
              {renderQueueBlock()}
              {signUpBanner}
              {renderSourceControlErrorBanner()}
              <ChatInput {...sessionlessChatInputProps} />
              {/* Show verb-based suggestions below the input on desktop */}
              {showAnimations && (
                <EvidenceVerbSuggestions onPopulateInput={handlePopulateInput} onSuggestionHover={setSuggestionPreview} onFillVerbClick={isAuthenticated ? handleFillVerbClick : undefined} />
              )}
            </div>
          </div>
        )
      ) : (
        <>
          {/* Conversation timeline - scrollable */}
          <div
            ref={conversationOverlayContainerRef}
            className="relative flex-1 overflow-hidden"
          >
            <div className="h-full transition-opacity duration-200 ease-in-out">
              {conversationContent}
            </div>
            <EvidenceSBSInline chatId={chatId ?? null} inSession={!isSessionless} />
            {blurOverlay}
            {sidePanel}
          </div>

          {/* Input area - fixed at bottom */}
          <div className="mx-auto mb-2 w-full max-w-3xl shrink-0 px-4 sm:mb-2">
            {renderQueueBlock('mb-3')}
            {signUpBanner}
            <SourceRecommendationBanner className="mb-3" />
            {IS_CHAT_CAPACITY_GUARD_ENABLED() && capacityStatus && capacityStatus !== 'ok' && (
              <ChatCapacityBanner
                capacityStatus={capacityStatus}
                onNewChat={capacityBannerHandler}
                isMigrating={isMigrating}
                className="mb-3"
              />
            )}
            {/* Trial source control: above suggestions peek when in-session or existing chat */}
            {(chatId || !isSessionless) && (
              <TrialSourceControlBanner position="inline" className="mb-3" />
            )}
            {/* Evidence Pro trial banner - above suggestions so it pushes conversation
                content up rather than displacing the suggestions sheet */}
            <EvidenceProTrialBanner />
            {/* Promo banner: only renders once the user passes a usage threshold and hasn't dismissed. */}
            <EvidencePowerUserBanner />
            <div
              ref={chatInputAreaRef}
              className={cn(
                IS_CHAT_CAPACITY_GUARD_ENABLED() && capacityStatus === 'exceeded' && 'pointer-events-none opacity-65'
              )}
            >
                <DocumentSuggestionBanner
                  sessionId={effectiveSessionId || undefined}
                  onCtaClick={handleDocumentSuggestionBannerCtaClick}
                  className={hidePeekAboveEvidenceInput ? 'mb-3' : '-mb-1'}
                />
                {/* Evidence prompts bar above input in sidebar mode (GRO-2829).
                    Source recommendation banner precedence is enforced inside
                    the hook via isBlocked. When transcript chips are killed,
                    prefer the shortcuts peek over this clinical prompts surface. */}
                {evidencePromptsBar.shouldShow &&
                  !!effectiveSessionId &&
                  !showShortcutsPeekChrome && (
                  <div className="-mb-3">
                    <EvidencePromptsBar
                      suggestions={evidencePromptsBar.suggestions}
                      sourceFavicons={evidencePromptsBar.sourceFavicons}
                      expanded={evidencePromptsBar.expanded}
                      onExpandedChange={evidencePromptsBar.onExpandedChange}
                      onSuggestionClick={handleChipSend}
                      onDismiss={evidencePromptsBar.onDismiss}
                    />
                  </div>
                )}
                {/* Legacy chips above input — suppressed when prompts bar or verb chips active.
                    When transcript chips are killed, prefer shortcuts peek over prompts bar. */}
                {(!evidencePromptsBar.shouldShow || showShortcutsPeekChrome) &&
                  !hidePeekAboveEvidenceInput &&
                  hasChipsToShow &&
                  !showInSessionVerbChips && (
                  <SuggestionsPeekSheet
                    chips={peekSuggestedChips}
                    pinnedChips={pinnedChips}
                    isTranscribing={peekIsTranscribing}
                    onChipClick={onChipClick ?? handleChipSend}
                    onPinnedChipClick={onPinnedChipClick ?? handleChipSend}
                  />
                )}
                {renderSourceControlErrorBanner('mb-3')}
                <div className="relative z-10">
                  <ChatInput
                    {...baseChatInputProps}
                    enableSourcesSpotlight={enableSourcesSpotlight}
                    inputState="follow up"
                    showInlineSuggestions={isEmpty && !isMobile}
                    suggestionsDirection="up"
                    populateValue={populateValue}
                    onPopulateConsumed={() => setPopulateValue(null)}
                    showDisclaimer
                    hideBadges
                    personalisedSourceLogos={personalisedSourceLogos}
                    analyticsSurface={analyticsSurface}
                  />
                </div>
              </div>
            {/* Trial source control: floating at bottom when sessionless + new chat */}
            {isSessionless && !chatId && (
              <TrialSourceControlBanner
                position="fixed-bottom"
                anchorRef={chatInputAreaRef}
              />
            )}
          </div>
        </>
      )}

      {/* Experience feedback nudge - appears after 2nd completed query (UMUX-Lite) */}
      {/* Hidden on mobile due to close button interaction issues */}
      {shouldShowNudge && !isMobile && (
        <ExperienceFeedbackNudge
          easeRating={feedbackEaseRating}
          usefulnessRating={feedbackUsefulnessRating}
          detail={feedbackDetail}
          isRatingSubmitted={isFeedbackRatingSubmitted}
          onEaseRatingSubmit={handleFeedbackEaseRatingSubmit}
          onUsefulnessRatingSubmit={handleFeedbackUsefulnessRatingSubmit}
          onDetailChange={setFeedbackDetail}
          onSubmit={() => void handleFeedbackSubmit()}
          onDismiss={handleFeedbackDismiss}
          isSubmitDisabled={isFeedbackSubmitDisabled}
        />
      )}

      {/* Debug panel - only visible in localhost */}
      <ChatDebugPanel
        mode={resolvedMode}
        sessionId={sessionId}
        chatId={chatId}
        isStreaming={isStreaming}
        onShowFeedbackNudge={showFeedbackNudge}
      />

      <FormTemplatePicker
        isOpen={isFormTemplatePickerOpen}
        onClose={closeFormTemplatePicker}
        onSelectTemplate={handleFormTemplateSelected}
        onLinkSession={handleLinkSessionForFill}
        needsSessionLink={isSessionless && !linkedSession}
      />
        </div>
      </div>
    </div>
    </ProseDensityProvider>
  );
};
